/**
 * A member's profile card (click a name or avatar): photo/avatar, name, @username, role badge, title, pronouns, local
 * time (with how far ahead/behind me), status, email/phone when visible, their user groups, and "Message" (or "Edit
 * profile" for me). The host also provides `GroupCardContext` and shows user group cards and the group editor
 * (UserGroupsSettings.tsx), so a group mention anywhere in the shell opens its card.
 * `MemberCardHost` provides `MemberCardContext` to everything inside it and shows the card. Native: a sheet / popover
 * presented by the same host idea (an environment action).
 */
import { Access, Dnd, Handles, Profile, Shell, UserGroups, type MessageDescriptor } from "@bundle/core";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { Avatar, Button, Chip, Icon, Sheet, Stack, Text } from "../../primitives/index.ts";
import { useWorkspace } from "../../hooks/index.ts";
import { useLocale, useT, useTr } from "../../i18n/I18nProvider.tsx";
import { GroupCardContext, MemberCardContext, useSettingsContext, type GroupCard, type MemberCard } from "../../context/SettingsContext.tsx";
import { AgentBadge, ExternalBadge } from "../Badges.tsx";
import { useStatusDisplay } from "../StatusEmoji.tsx";
import { FieldLabel, RoleBadge } from "./SettingsKit.tsx";
import { UserGroupSheets, useUserGroups } from "./UserGroupsSettings.tsx";
import { useProfileBlock } from "../../moderation/Moderation.tsx";

function InfoLine({ icon, text, testID }: { icon: Parameters<typeof Icon>[0]["name"]; text: string; testID: string }) {
  return (
    <Stack direction="horizontal" align="center" gap="2" testID={testID}>
      <Icon name={icon} size="icon.sm" color="text.tertiary" />
      <Stack grow>
        <Text variant="footnote" color="text.secondary" lines={1} selectable>
          {text}
        </Text>
      </Stack>
    </Stack>
  );
}

export type MemberCardHostProps = {
  /** Opens a URL in the focused pane (the DM, my profile settings). */
  onOpenUrl: (url: string) => void;
  children?: ReactNode;
};

export function MemberCardHost({ onOpenUrl, children }: MemberCardHostProps) {
  const t = useT();
  const tr = useTr();
  const locale = useLocale();
  const ws = useWorkspace();
  const settings = useSettingsContext();
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const card = useMemo<MemberCard>(() => ({ open: (id: string) => setUserId(id) }), []);
  const close = useCallback(() => setUserId(undefined), []);
  const groups = useUserGroups();
  const groupVm = groups.vm;
  const groupCard = useMemo<GroupCard>(() => ({ open: (id: string) => groupVm.openCard(id) }), [groupVm]);
  const cache = ws.state.cache;
  const m = userId === undefined ? undefined : cache?.members[userId];
  const now = ws.vm.deps.clock.now();
  const me = cache?.members[ws.state.meId];
  const isMe = m !== undefined && m.userId === ws.state.meId;

  let time: { time: string; difference?: MessageDescriptor } | undefined;
  if (m?.timezone !== undefined && settings !== null) {
    const theirs = settings.deps.zones.offsetMinutes(m.timezone, now);
    if (theirs !== undefined) time = Profile.localTimeLabel(now, theirs, ws.vm.deps.clock.tzOffsetMinutes(now), locale);
  }
  // The status on top (Slack's profile): emoji, text and "Until …"; gone the moment it expires.
  const status = useStatusDisplay(m);
  const paused = m !== undefined && m.kind === "human" && Dnd.memberPaused(m, now);
  const look = m === undefined ? {} : Profile.avatarLook(m);
  const canMessage = m !== undefined && me !== undefined && !m.deactivated && !isMe;
  const org = m === undefined ? undefined : Access.organizationName(m);
  const memberGroups = m === undefined ? [] : UserGroups.groupsOf(m.userId, Object.values(cache?.userGroups ?? {}));
  // Block / Unblock (Report & block; nothing for me, apps or agents).
  const block = useProfileBlock(m?.userId);

  return (
    <MemberCardContext.Provider value={card}>
      <GroupCardContext.Provider value={groupCard}>
      {children}
      <Sheet open={m !== undefined} onClose={close} title={m === undefined ? "" : t("profileCard.title", { name: m.displayName })} closeLabel={t("common.close")} testID="member-card">
        {m === undefined ? null : (
          <Stack gap="4">
            <Stack direction="horizontal" align="center" gap="4">
              <Avatar name={m.displayName} src={look.src} tone={look.tone} emoji={look.emoji} square={m.kind === "agent"} size="avatar.xl" dnd={paused} dndLabel={t("dnd.paused")} />
              <Stack grow gap="0.5">
                <Text variant="title3" weight="bold" lines={1} testID="member-card-name">
                  {m.displayName}
                </Text>
                {m.handle === undefined ? null : (
                  <Text color="text.secondary" lines={1} testID="member-card-handle">
                    {Handles.formatHandle(m.handle)}
                  </Text>
                )}
                <Stack direction="horizontal" align="center" gap="1.5" wrap>
                  <RoleBadge member={m} primaryOwnerId={cache?.workspace.primaryOwnerId} />
                  {m.kind === "agent" ? <AgentBadge /> : null}
                  {org === undefined ? null : <ExternalBadge organization={org} />}
                  {m.deactivated ? (
                    <Text variant="caption" color="text.tertiary">
                      {t("members.status.deactivated")}
                    </Text>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
            {m.fullName !== m.displayName || m.title !== undefined || m.pronouns !== undefined ? (
              <Stack gap="0.5">
                {m.fullName !== m.displayName ? <Text weight="medium">{m.fullName}</Text> : null}
                {m.title === undefined ? null : <Text color="text.secondary">{m.title}</Text>}
                {m.pronouns === undefined ? null : (
                  <Text variant="footnote" color="text.tertiary">
                    {m.pronouns}
                  </Text>
                )}
              </Stack>
            ) : null}
            {status === undefined ? null : (
              <Stack direction="horizontal" align="start" gap="2.5" padding="3" radius="lg" background="bg.hover" testID="member-card-status">
                <Text variant="title3">{status.emoji}</Text>
                <Stack grow gap="0.5">
                  {status.text === "" ? null : <Text weight="semibold">{status.text}</Text>}
                  {status.until === undefined ? null : (
                    <Text variant="footnote" color="text.secondary" testID="member-card-status-until">
                      {tr(status.until)}
                    </Text>
                  )}
                </Stack>
              </Stack>
            )}
            <Stack gap="1.5">
              {time === undefined ? null : (
                <InfoLine
                  icon="clock"
                  text={time.difference === undefined ? t("profileCard.localTime", { time: time.time }) : `${t("profileCard.localTime", { time: time.time })} · ${tr(time.difference)}`}
                  testID="member-card-time"
                />
              )}
              {paused ? <InfoLine icon="moon" text={t("dnd.paused")} testID="member-card-dnd" /> : null}
              {m.email === undefined ? null : <InfoLine icon="mail" text={m.email} testID="member-card-email" />}
              {m.phone === undefined ? null : <InfoLine icon="message" text={m.phone} testID="member-card-phone" />}
            </Stack>
            {memberGroups.length === 0 ? null : (
              <Stack gap="1.5" testID="member-card-groups">
                <FieldLabel label={t("profile.userGroups")} />
                <Stack direction="horizontal" gap="1.5" wrap>
                  {memberGroups.map((g) => (
                    <Chip
                      key={g.id}
                      label={`@${g.handle}`}
                      icon="users"
                      onPress={() => {
                        close();
                        groupVm.openCard(g.id);
                      }}
                      testID={`member-card-group-${g.id}`}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            <Stack direction="horizontal" gap="2">
              {canMessage ? (
                <Button
                  label={t("profileCard.message")}
                  variant="primary"
                  icon="message"
                  onPress={() => {
                    close();
                    onOpenUrl(Shell.formatUrl({ type: "dm", ws: ws.slug, userId: m.userId }));
                  }}
                  testID="member-card-message"
                />
              ) : null}
              {isMe ? (
                <Button
                  label={t("profileCard.editProfile")}
                  variant="secondary"
                  icon="edit"
                  onPress={() => {
                    close();
                    onOpenUrl(Shell.formatUrl({ type: "settings", ws: ws.slug, section: "profile" }));
                  }}
                  testID="member-card-edit"
                />
              ) : null}
              {block === undefined ? null : (
                <Button
                  label={block.label}
                  variant="secondary"
                  icon="ban"
                  onPress={() => {
                    if (!block.blocked) close();
                    block.onPress();
                  }}
                  testID={block.blocked ? "member-card-unblock" : "member-card-block"}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Sheet>
      <UserGroupSheets
        vm={groupVm}
        state={groups.state}
        onOpenMember={(id) => {
          groupVm.closeCard();
          setUserId(id);
        }}
        onOpenChannel={(id) => {
          groupVm.closeCard();
          onOpenUrl(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: id }));
        }}
      />
      </GroupCardContext.Provider>
    </MemberCardContext.Provider>
  );
}
